import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  KNOWN_IMPRESSION_TYPES,
  KnownImpressionTypes,
} from '../../../SubjectViewer/Modules/AITools/ImpressionEditor/ImpressionItem';
import { useTemplatingItemTypeDisplay } from '../../hooks';
import { useEditTemplatingItemMetadataForm } from '../../hooks/useEditTemplatingItemMetadataForm';
import { ImpressionTypeFilter } from '../../ImpressionTypeFilter';
import { TemplatingItem } from '../../models';

const messages = defineMessages({
  modalTitle: {
    id: 'RenameTemplatingItemModal.title',
    defaultMessage: 'Rename {typeName}',
  },
  templatingItemNameLabel: {
    id: 'RenameTemplatingItemModal.templatingItemName',
    defaultMessage: '{typeName} name',
  },
  saveButton: {
    id: 'RenameTemplatingItemModal.save',
    defaultMessage: 'Save',
  },
  cancelButton: {
    id: 'RenameTemplatingItemModal.cancel',
    defaultMessage: 'Cancel',
  },
});

const useStyles = makeStyles(theme => ({
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

type EditTemplatingItemMetadataModalProps = {
  templatingItem: TemplatingItem;
  isOpen: boolean;
  onSuccess?: () => void;
  onDismiss: () => void;
};

export const EditTemplatingItemMetadataModal: React.FC<
  EditTemplatingItemMetadataModalProps
> = ({ templatingItem, isOpen, onSuccess, onDismiss }) => {
  const styles = useStyles();
  const { formatMessage } = useIntl();
  const templatingItemTypeDisplay = useTemplatingItemTypeDisplay();

  const {
    templatingItemNameControl,
    impressionTypeControl,
    onSubmit,
    hasError,
    reset,
    isDirty,
  } = useEditTemplatingItemMetadataForm({
    templatingItem,
    onSuccess,
  });

  const handleDismiss = useCallback(() => {
    reset();
    onDismiss();
  }, [reset, onDismiss]);

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleDismiss}
      size={1}
      title={formatMessage(messages.modalTitle, {
        typeName: templatingItemTypeDisplay[templatingItem.type],
      })}
      actions={
        <>
          <Button variant="text" color="negative" onClick={handleDismiss}>
            {formatMessage(messages.cancelButton)}
          </Button>
          <Button
            variant="contained"
            onClick={() => onSubmit()}
            disabled={hasError || !isDirty}
          >
            {formatMessage(messages.saveButton)}
          </Button>
        </>
      }
    >
      <div className={styles.modalContent}>
        <TextField
          label={formatMessage(messages.templatingItemNameLabel, {
            typeName: templatingItemTypeDisplay[templatingItem.type],
          })}
          aria-label={formatMessage(messages.templatingItemNameLabel, {
            typeName: templatingItemTypeDisplay[templatingItem.type],
          })}
          {...templatingItemNameControl.field}
          errorMessage={templatingItemNameControl.fieldState.error?.message}
          hasError={!!templatingItemNameControl.fieldState.error}
          fullWidth
        />
        {templatingItem.type === 'template' && (
          <ImpressionTypeFilter
            impressionTypes={KNOWN_IMPRESSION_TYPES as KnownImpressionTypes[]}
            value={impressionTypeControl.field.value}
            onChange={impressionTypeControl.field.onChange}
            disabled={false}
            extraSections={[]}
            variant="form-field"
          />
        )}
      </div>
    </Modal>
  );
};
